/** The main rail items in the person's order: drag to reorder, or use Move up and Move down (keyboard and touch), with a switch to show or hide each. */
import { useCallback, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { ChevronDown, ChevronUp, GripVertical } from '@/shared/ui/icons';
import { SettingsTouchSwitch as Switch } from '../../settings/SettingsTouchSwitch';
import { cn } from '../../../shared/ui/utils';
import { isGroupEdge, moveItemWithinGroup } from '../../../platform/navigation/mainRailOrder';

export interface RailOrderItem {
  id: string;
  label: string;
  icon?: React.ElementType;
  /** The rail group this item renders in; items only reorder inside their own group. */
  group?: string;
}

const DRAG_TYPE = 'PREFERENCES_RAIL_ITEM';

interface DragItem { id: string }

interface RowProps {
  item: RailOrderItem;
  canMoveUp: boolean;
  canMoveDown: boolean;
  index: number;
  visible: boolean;
  pinned: boolean;
  onMove: (fromId: string, toIndex: number) => void;
  onDrop: () => void;
  onDragCancel: () => void;
  onShift: (id: string, delta: -1 | 1) => void;
  onToggle: (id: string, hidden: boolean) => void;
}

function RailOrderRow({ item, index, canMoveUp, canMoveDown, visible, pinned, onMove, onDrop, onDragCancel, onShift, onToggle }: RowProps) {
  const rowRef = useRef<HTMLLIElement>(null);
  const Icon = item.icon;
  const labelId = `rail-order-${item.id}`;

  const [{ isDragging }, drag, preview] = useDrag<DragItem, unknown, { isDragging: boolean }>({
    type: DRAG_TYPE,
    item: { id: item.id },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    end: (_item, monitor) => { if (monitor.didDrop()) onDrop(); else onDragCancel(); },
  });
  const [, drop] = useDrop<DragItem>({
    accept: DRAG_TYPE,
    hover: (dragged) => { if (dragged.id !== item.id) onMove(dragged.id, index); },
  });
  preview(drop(rowRef));

  return (
    <li
      ref={rowRef}
      data-rail-order-item={item.id}
      className={cn('flex items-center gap-2 border-b border-border py-1.5 last:border-b-0', isDragging && 'opacity-40')}
    >
      <span
        ref={(node) => { drag(node); }}
        aria-hidden="true"
        className="hidden size-6 shrink-0 cursor-grab items-center justify-center text-muted-foreground lg:flex"
      >
        <GripVertical className="size-4" />
      </span>
      {Icon ? <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" /> : <span className="size-5 shrink-0" />}
      <span id={labelId} className={cn('min-w-0 flex-1 truncate text-sm', !visible && 'text-muted-foreground')}>{item.label}</span>
      <button
        type="button"
        aria-label={`Move ${item.label} up`}
        disabled={!canMoveUp}
        onClick={() => onShift(item.id, -1)}
        className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-[rgba(118,118,128,0.12)] disabled:pointer-events-none disabled:opacity-30"
      >
        <ChevronUp className="size-4" />
      </button>
      <button
        type="button"
        aria-label={`Move ${item.label} down`}
        disabled={!canMoveDown}
        onClick={() => onShift(item.id, 1)}
        className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-[rgba(118,118,128,0.12)] disabled:pointer-events-none disabled:opacity-30"
      >
        <ChevronDown className="size-4" />
      </button>
      <Switch
        aria-labelledby={labelId}
        checked={pinned || visible}
        disabled={pinned}
        onCheckedChange={(checked: boolean) => onToggle(item.id, !checked)}
      />
    </li>
  );
}

export function RailOrderList({
  items,
  hiddenSet,
  pinnedIds,
  onOrderChange,
  onToggle,
  onReset,
  canReset,
}: {
  /** Every rail item, already in the person's order. */
  items: readonly RailOrderItem[];
  hiddenSet: ReadonlySet<string>;
  pinnedIds: readonly string[];
  onOrderChange: (ids: string[]) => void;
  onToggle: (id: string, hidden: boolean) => void;
  onReset: () => void;
  canReset: boolean;
}) {
  // While a drag is in flight the order lives here; it is saved once, when the item is dropped.
  const [dragOrder, setDragOrder] = useState<string[] | null>(null);
  const dragOrderRef = useRef<string[] | null>(null);
  const baseIds = items.map((item) => item.id);
  const groupById = new Map(items.map((item) => [item.id, item.group] as const));
  // A pinned item is a group of its own, so it never moves and nothing moves past it.
  const groupOf = (id: string) => (pinnedIds.includes(id) ? `pinned:${id}` : groupById.get(id));

  const setDrag = (next: string[] | null) => {
    dragOrderRef.current = next;
    setDragOrder(next);
  };

  const onMove = useCallback((fromId: string, toIndex: number) => {
    const current = dragOrderRef.current ?? baseIds;
    const from = current.indexOf(fromId);
    if (from < 0 || from === toIndex) return;
    // Dropping onto another group is ignored; the rail renders each group on its own.
    setDrag(moveItemWithinGroup(current, from, toIndex, groupOf));
  // baseIds and groupOf change identity each render; their content is what matters and is read at call time.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseIds.join('\n'), items.map((item) => item.group).join('\n'), pinnedIds.join('\n')]);

  const onDrop = useCallback(() => {
    const finished = dragOrderRef.current;
    setDrag(null);
    if (finished) onOrderChange(finished);
  }, [onOrderChange]);

  const onShift = (id: string, delta: -1 | 1) => {
    const from = baseIds.indexOf(id);
    if (from < 0) return;
    onOrderChange(moveItemWithinGroup(baseIds, from, from + delta, groupOf));
  };

  const byId = new Map(items.map((item) => [item.id, item] as const));
  const shownIds = dragOrder ?? baseIds;
  const shown = shownIds.map((id) => byId.get(id)).filter((item): item is RailOrderItem => !!item);

  return (
    <div className="min-w-0">
      <ul data-slot="rail-order-list" aria-label="Main rail items">
        {shown.map((item, index) => (
          <RailOrderRow
            key={item.id}
            item={item}
            index={index}
            canMoveUp={!isGroupEdge(shownIds, index, -1, groupOf)}
            canMoveDown={!isGroupEdge(shownIds, index, 1, groupOf)}
            visible={!hiddenSet.has(item.id)}
            pinned={pinnedIds.includes(item.id)}
            onMove={onMove}
            onDrop={onDrop}
            onDragCancel={() => setDrag(null)}
            onShift={onShift}
            onToggle={onToggle}
          />
        ))}
      </ul>
      <button
        type="button"
        disabled={!canReset}
        onClick={onReset}
        className="mt-3 text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:pointer-events-none disabled:opacity-40"
      >
        Reset to default
      </button>
    </div>
  );
}
